import React from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import DescriptionIcon from '@mui/icons-material/Description';
import StatusChip from './StatusChip';
import DuplicateBadge from './DuplicateBadge';
import { formatDateTime, formatDocumentType } from '../../utils/scanReportUtils';

/**
 * DocumentOverviewCard displays a high-level summary of the uploaded PDF:
 * name, status, page count, how many documents were found, and any warnings.
 *
 * @param {Object} props
 * @param {Object} props.details - main_document_details from the JSON
 * @param {Object} props.summary - main_document_summary from the JSON
 * @param {Array}  [props.warnings] - main_document_warnings array
 */
const DocumentOverviewCard = ({ details, summary, warnings = [] }) => {
  const {
    documentName,
    numPages,
    status,
    displayStatus,
    created,
    createdBy,
    pdfHash,
  } = details || {};

  const {
    total_documents_found = 0,
    document_types = {},
    total_warnings = 0,
  } = summary || {};

  // Build document type summary chips
  const docTypeSummary = Object.entries(document_types).map(([type, count]) => ({
    label: `${count} ${formatDocumentType(type)}${count !== 1 ? 's' : ''}`,
    type,
  }));

  return (
    <Card variant="outlined">
      <CardContent>
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <DescriptionIcon color="primary" sx={{ fontSize: 36, mt: 0.25 }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={600} noWrap title={documentName}>
              {documentName || 'Unnamed Document'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Uploaded by {createdBy || 'unknown'} on {formatDateTime(created)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <StatusChip status={status} label={displayStatus || undefined} />
            {status === 'DUPLICATE' && (
              <DuplicateBadge duplicateReason={details?.duplicateReason || 'HASH_MATCH'} />
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Stats row */}
        <Box
          sx={{
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          <StatItem label="Total Pages" value={numPages} />
          <StatItem label="Documents Found" value={total_documents_found} />
          <StatItem
            label="Warnings"
            value={total_warnings}
            color={total_warnings > 0 ? 'warning.main' : 'text.primary'}
          />
        </Box>

        {/* Document type breakdown */}
        {docTypeSummary.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {docTypeSummary.map(({ label, type }) => (
              <Typography
                key={type}
                variant="body2"
                color="text.secondary"
                sx={{
                  bgcolor: 'grey.100',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            {warnings.map((warning, idx) => (
              <Alert key={idx} severity="warning" variant="outlined">
                {typeof warning === 'string' ? warning : JSON.stringify(warning)}
              </Alert>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

/** Small stat display used within the overview card. */
const StatItem = ({ label, value, color = 'text.primary' }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={600} color={color}>
      {value ?? '—'}
    </Typography>
  </Box>
);

StatItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
};

DocumentOverviewCard.propTypes = {
  details: PropTypes.shape({
    documentName: PropTypes.string,
    numPages: PropTypes.number,
    status: PropTypes.string,
    displayStatus: PropTypes.string,
    created: PropTypes.string,
    createdBy: PropTypes.string,
    pdfHash: PropTypes.string,
    duplicateReason: PropTypes.string,
  }),
  summary: PropTypes.shape({
    total_documents_found: PropTypes.number,
    total_segments_processed: PropTypes.number,
    document_types: PropTypes.object,
    total_warnings: PropTypes.number,
  }),
  warnings: PropTypes.array,
};

export default DocumentOverviewCard;